const ENC_PREFIX = "enc:v1:";
const KEY_IDB = "mycoaching_aes_key";
const DB_NAME = "mycoaching_crypto";
const DB_STORE = "keys";

let cryptoKey = null;
let initPromise = null;

function getDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(DB_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }).catch(() => null);
}

async function idbGet(key) {
  const db = await getDb();
  if (!db) return null;
  return new Promise((resolve) => {
    const tx = db.transaction(DB_STORE, "readonly");
    const req = tx.objectStore(DB_STORE).get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => resolve(null);
  });
}

async function idbSet(key, value) {
  const db = await getDb();
  if (!db) return;
  await new Promise((resolve) => {
    const tx = db.transaction(DB_STORE, "readwrite");
    tx.objectStore(DB_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

async function loadOrCreateKey() {
  if (!window.crypto?.subtle) return null;

  let raw = await idbGet(KEY_IDB);
  if (!raw) {
    raw = crypto.getRandomValues(new Uint8Array(32));
    await idbSet(KEY_IDB, Array.from(raw));
  } else {
    raw = new Uint8Array(raw);
  }

  return crypto.subtle.importKey("raw", raw, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

export function isEncryptedValue(value) {
  return typeof value === "string" && value.startsWith(ENC_PREFIX);
}

export async function ensureTokenCrypto() {
  if (cryptoKey) return cryptoKey;
  if (!initPromise) {
    initPromise = loadOrCreateKey().then((key) => {
      cryptoKey = key;
      return key;
    });
  }
  return initPromise;
}

function bytesToB64(bytes) {
  let binary = "";
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary);
}

function b64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export async function encryptToken(plain) {
  if (!plain) return plain;
  const key = await ensureTokenCrypto();
  if (!key) return plain;

  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(plain);
  const cipher = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoded);
  return `${ENC_PREFIX}${bytesToB64(iv)}.${bytesToB64(new Uint8Array(cipher))}`;
}

export async function decryptToken(stored) {
  if (!stored) return stored;
  if (!isEncryptedValue(stored)) return stored;

  const key = await ensureTokenCrypto();
  if (!key) return stored;

  try {
    const payload = stored.slice(ENC_PREFIX.length);
    const [ivB64, cipherB64] = payload.split(".");
    if (!ivB64 || !cipherB64) return stored;

    const iv = b64ToBytes(ivB64);
    const cipher = b64ToBytes(cipherB64);
    const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, cipher);
    return new TextDecoder().decode(plain);
  } catch {
    return null;
  }
}